/**
 * Backoffice CRM: where an inbox thread stands (#1529, customer_inbox_spec.md § Snooze, § Customer timeline).
 *
 * On the thread's page: snoozing until a date (the panel, and the banner while it runs), the GitHub tickets the
 * thread relates to (links only: the inbox doesn't read GitHub, so they never wake it), the history (every status
 * change, reply, snooze, wake-up and note, with who and when), and the customer's timeline (their other threads,
 * waitlist entry and campaign emails, in-app feedback). Changes go through callables (inbox_thread_service.js).
 */
import { escapeHTML } from "./familyDetailsPanel.js";
import { withBusy } from "./ui/busy.js";
import { historyHref } from "./historyController.js";

const REPO_ISSUES = "https://github.com/anton2lukas1/weeknightbite/issues/";

let ctx = null;
let thread = null;
let onChange = null;
/** What was read for the open thread: its events and its customer's timeline. Refetched on open and after a change. */
let cache = { threadId: null, events: null, timeline: null };

const SMALL = "rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20";
const PRIMARY = "rounded-md bg-paprika-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-paprika-500 disabled:opacity-50";
const INPUT = "rounded-md bg-white px-2 py-1 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10";

function at9(date) {
  const d = new Date(date);
  d.setHours(9, 0, 0, 0);
  return d;
}

/** The quick choices, in the admin's own time zone: tomorrow, next Monday and a week from now, each at 09:00. */
export function snoozePresets(now = new Date()) {
  const tomorrow = at9(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const daysToMonday = ((8 - now.getDay()) % 7) || 7;
  const monday = at9(new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToMonday));
  const week = at9(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7));
  return [
    { key: "tomorrow", label: "Tomorrow", date: tomorrow },
    { key: "monday", label: "Monday", date: monday },
    { key: "week", label: "In a week", date: week }
  ];
}

export function formatWhen(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function snoozePanelHTML(now = new Date()) {
  const presets = snoozePresets(now).map((p) => `<button type="button" data-snooze-preset="${p.date.toISOString()}" class="${SMALL}">${p.label} <span class="font-normal text-gray-500 dark:text-gray-400">${escapeHTML(formatWhen(p.date.toISOString()))}</span></button>`).join("");
  return `<p class="text-sm font-semibold text-gray-900 dark:text-white">Snooze until…</p>
    <div class="mt-2 flex flex-wrap items-center gap-2">${presets}
      <input data-snooze-custom type="datetime-local" aria-label="Another date and time" class="${INPUT}" />
    </div>
    <input data-snooze-note type="text" maxlength="2000" placeholder="Why, e.g. check in after his first week" aria-label="Snooze note" class="mt-2 w-full ${INPUT}" />
    <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">It comes back to Open or Waiting at that time, or sooner if the customer writes.</p>
    <div class="mt-2 flex justify-end gap-2"><button type="button" data-snooze-cancel class="${SMALL}">Cancel</button><button type="button" data-snooze-custom-go class="${PRIMARY}">Snooze until the date above</button></div>`;
}

/** The banner while a thread is snoozed. */
export function snoozeBannerHTML(t) {
  if (!t || t.status !== "snoozed" || !t.snooze) return "";
  const note = t.snooze.note ? ` · ${escapeHTML(t.snooze.note)}` : "";
  return `<span><span class="font-semibold">Snoozed until ${escapeHTML(formatWhen(t.snooze.until))}</span>${note}</span>
    <button type="button" data-unsnooze class="rounded-md bg-white/70 px-2.5 py-1 text-sm font-semibold text-amber-900 hover:bg-white disabled:opacity-50 dark:bg-white/10 dark:text-amber-100 dark:hover:bg-white/20">Unsnooze</button>`;
}

/** Linked tickets: links to GitHub, removable, and a field to add one. */
export function issuesHTML(issues = []) {
  const chips = issues.map((n) => `<span class="inline-flex items-center gap-1 rounded-md bg-gray-100 py-0.5 pr-1 pl-2 text-sm dark:bg-white/10">
      <a href="${REPO_ISSUES}${Number(n)}" target="_blank" rel="noopener noreferrer" class="font-medium text-paprika-700 hover:underline dark:text-paprika-400">#${Number(n)}</a>
      <button type="button" data-issue-remove="${Number(n)}" aria-label="Unlink #${Number(n)}" class="inline-flex items-center rounded p-0.5 text-gray-400 hover:text-red-600 disabled:opacity-50"><svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="size-3.5"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg></button>
    </span>`).join("");
  return `<div class="flex flex-wrap items-center gap-2">${chips || `<span class="text-sm text-gray-500 dark:text-gray-400">None linked.</span>`}
    <input data-issue-input type="text" inputmode="numeric" placeholder="#1379" aria-label="Link a ticket by number" class="w-24 ${INPUT}" />
    <button type="button" data-issue-add class="${SMALL}">Link</button></div>
    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">For reference: a ticket doesn't wake the thread.</p>`;
}

function who(by) {
  if (!by) return "";
  if (by.system) return "the sync";
  return String(by.name || by.email || by.uid || "").split(/\s+/)[0];
}

const STATUS_WORDS = { open: "Open", waiting: "Waiting", handled: "Handled", spam: "Spam", snoozed: "Snoozed" };
const WAKE_REASONS = { date: "its date came", customer: "the customer wrote", admin: "unsnoozed" };

/** One line of history, escaped. */
export function eventText(e) {
  const name = escapeHTML(who(e.by));
  if (e.type === "status") return `${name} moved it from ${STATUS_WORDS[e.from] || "—"} to ${STATUS_WORDS[e.to] || escapeHTML(e.to)}`;
  if (e.type === "reply") return `${name} ${e.new_message ? "sent a new message" : "replied"}${e.saved_to_sent === false ? " (the copy didn't reach Sent)" : ""}`;
  if (e.type === "reply_elsewhere") return `${name} answered it in another conversation`;
  if (e.type === "snooze") return `${name} snoozed it until ${escapeHTML(formatWhen(e.until))}${e.note ? `: “${escapeHTML(e.note)}”` : ""}`;
  if (e.type === "wake") return `Woke up: ${WAKE_REASONS[e.reason] || escapeHTML(e.reason || "")}${e.reason === "admin" ? ` by ${name}` : ""}, back to ${STATUS_WORDS[e.to] || escapeHTML(e.to || "")}`;
  if (e.type === "issues") {
    const parts = [];
    if (e.added && e.added.length) parts.push(`linked ${e.added.map((n) => `#${Number(n)}`).join(", ")}`);
    if (e.removed && e.removed.length) parts.push(`unlinked ${e.removed.map((n) => `#${Number(n)}`).join(", ")}`);
    return `${name} ${parts.join(" and ")}`;
  }
  if (e.type === "customer_link") {
    return e.to
      ? `${name} linked it to ${escapeHTML(e.to.email || e.to.uid || "an account")}`
      : `${name} unlinked it from its account`;
  }
  if (e.type === "note") return `<span class="font-medium">${name}</span>: <span class="whitespace-pre-wrap">${escapeHTML(e.text || "")}</span>`;
  return escapeHTML(e.type || "");
}

export function historyHTML(events) {
  if (!events) return `<p class="text-sm text-gray-500 dark:text-gray-400">Loading the history…</p>`;
  const items = events.length
    ? events.map((e) => `<li class="flex gap-3 text-sm ${e.type === "note" ? "rounded-md bg-amber-50 px-2 py-1.5 dark:bg-amber-500/10" : ""}">
        <span class="w-40 shrink-0 text-xs/5 text-gray-500 dark:text-gray-400">${escapeHTML(formatWhen(e.at))}</span>
        <span class="min-w-0 text-gray-700 dark:text-gray-300">${eventText(e)}</span></li>`).join("")
    : `<li class="text-sm text-gray-500 dark:text-gray-400">Nothing yet.</li>`;
  return `<ol class="flex flex-col gap-1.5">${items}</ol>
    <div class="mt-3 flex items-start gap-2">
      <textarea data-note-input rows="2" maxlength="2000" placeholder="Add a note for the other admin (never sent)" aria-label="Note" class="min-w-0 flex-1 ${INPUT}"></textarea>
      <button type="button" data-note-add class="${SMALL}">Add note</button>
    </div>`;
}

function emailLine(label, e) {
  if (!e || !e.sent_at) return "";
  const opened = e.opened_at ? ` · opened ${escapeHTML(formatWhen(e.opened_at))}` : e.last_event ? ` · ${escapeHTML(e.last_event)}` : "";
  const note = e.founders_note ? `<blockquote class="mt-1 border-l-2 border-gray-300 pl-2 text-gray-600 whitespace-pre-wrap dark:border-white/20 dark:text-gray-400">${escapeHTML(e.founders_note)}</blockquote>` : "";
  return `<li>${label} sent ${escapeHTML(formatWhen(e.sent_at))}${opened}${note}</li>`;
}

/** The customer's timeline: other threads (open in place), waitlist and campaign emails, in-app feedback. */
export function timelineHTML(t, { withThreads = true } = {}) {
  if (!t) return `<p class="text-sm text-gray-500 dark:text-gray-400">Loading what we know about them…</p>`;
  const parts = [];
  if (withThreads && t.threads.length) {
    parts.push(`<div><h4 class="text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Other conversations</h4><ul class="mt-1 flex flex-col gap-1">${t.threads.map((x) => `<li><button type="button" data-open-thread="${escapeHTML(x.id)}" class="text-left text-sm text-paprika-700 hover:underline dark:text-paprika-400">${escapeHTML(x.subject || "(no subject)")}</button> <span class="text-xs text-gray-500 dark:text-gray-400">${escapeHTML(STATUS_WORDS[x.status] || x.status)} · ${escapeHTML(formatWhen(x.last_message_at))}</span></li>`).join("")}</ul></div>`);
  }
  if (t.lead) {
    const l = t.lead;
    parts.push(`<div><h4 class="text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Waitlist</h4><ul class="mt-1 flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
      <li>Joined ${escapeHTML(formatWhen(l.created_at))}${l.recipe_count ? ` with ${Number(l.recipe_count)} recipe link(s)` : ""}${l.claimed_at ? ` · recipes claimed ${escapeHTML(formatWhen(l.claimed_at))}` : ""}</li>
      ${emailLine("Welcome email", l.welcome_email)}${emailLine("Launch email", l.launch_promo_email)}</ul></div>`);
  }
  if (t.feedback.length) {
    parts.push(`<div><h4 class="text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">In-app feedback</h4><ul class="mt-1 flex flex-col gap-1.5">${t.feedback.map((f) => `<li class="text-sm text-gray-700 dark:text-gray-300"><span class="text-xs text-gray-500 dark:text-gray-400">${escapeHTML(formatWhen(f.created_at))}${f.handled ? " · handled" : ""}${f.topics.length ? ` · ${escapeHTML(f.topics.join(", "))}` : ""}</span><br><span class="whitespace-pre-wrap">${escapeHTML(f.note)}</span></li>`).join("")}</ul></div>`);
  }
  return parts.length ? `<div class="flex flex-col gap-4">${parts.join("")}</div>`
    : `<p class="text-sm text-gray-500 dark:text-gray-400">No ${withThreads ? "other conversations, " : ""}waitlist entry or in-app feedback from ${escapeHTML(t.email || "them")}.</p>`;
}

const REVIEW_SOURCES = new Set(["app_store", "google_play"]);

/**
 * Above the customer's timeline: the link to their whole contact history (#1925), and on a store review, which account
 * it is linked to by hand, or a field to link one. A review carries no address, so nothing links it on its own.
 */
export function customerLinkHTML(t) {
  if (!t) return "";
  const person = t.user_uid ? { uid: t.user_uid } : t.customer_email ? { email: t.customer_email } : null;
  const historyLink = person
    ? `<a href="${escapeHTML(historyHref(person))}" data-history-link class="text-sm font-semibold text-paprika-700 hover:underline dark:text-paprika-400">Full contact history →</a>`
    : "";
  if (!REVIEW_SOURCES.has(t.source)) return historyLink ? `<div class="mb-4">${historyLink}</div>` : "";
  const linked = t.user_uid
    ? `<p class="text-sm text-gray-700 dark:text-gray-300">Linked to <span class="font-medium">${escapeHTML(t.linked_customer?.email || t.user_uid)}</span>${t.linked_customer?.by ? ` by ${escapeHTML(who(t.linked_customer.by))}` : ""}.
         <button type="button" data-customer-unlink class="ml-1 text-sm font-semibold text-gray-500 hover:text-red-600 disabled:opacity-50 dark:text-gray-400">Unlink</button></p>`
    : `<p class="text-sm text-gray-500 dark:text-gray-400">A store review names no address. If you know whose it is, link it to their account and it shows in their history.</p>
       <div class="mt-2 flex items-center gap-2">
         <input data-customer-link-input type="text" autocomplete="off" spellcheck="false" placeholder="Email or user ID" aria-label="Their email or user ID" class="min-w-0 flex-1 ${INPUT}" />
         <button type="button" data-customer-link class="${SMALL}">Link</button>
       </div>`;
  return `<div class="mb-4 flex flex-col gap-2">${linked}${historyLink}</div>`;
}

/** The sections that go under the messages: tickets and history. The customer's timeline goes in the customer panel. */
export function extrasHTML(t, events) {
  return `<section class="mt-8"><h3 class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Tickets</h3><div data-extras-issues>${issuesHTML(t.linked_issues || [])}</div></section>
    <section class="mt-8"><h3 class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">History</h3><div data-extras-history>${historyHTML(events)}</div></section>`;
}

function call(name, data) {
  const { functions, httpsCallable } = ctx || {};
  return httpsCallable(functions, name)(data).then((res) => res.data || {});
}

/**
 * Draws the extras for the open thread into #inboxThreadExtras and #inboxCustomerTimeline, the snooze banner, and
 * binds them. Reads the history and the timeline once per thread opening (`refetch`), and redraws from what it read
 * when the page only re-renders (a toggle).
 */
export async function mountThreadExtras(options) {
  ctx = options.ctx;
  thread = options.thread;
  onChange = options.onChange;
  const sameThread = cache.threadId === thread.id;
  if (!sameThread || options.refetch) cache = { threadId: thread.id, events: sameThread ? cache.events : null, timeline: sameThread ? cache.timeline : null };
  draw();
  bindHeader();
  if (sameThread && !options.refetch && cache.events && cache.timeline) return;
  const id = thread.id;
  const [events, timeline] = await Promise.all([loadEvents(id), thread.automated ? Promise.resolve(null) : loadTimeline(id)]);
  if (cache.threadId !== id) return;
  cache.events = events;
  cache.timeline = timeline;
  draw();
}

function draw() {
  const extras = document.getElementById("inboxThreadExtras");
  if (extras) {
    extras.innerHTML = extrasHTML(thread, cache.events);
    extras.querySelector("[data-issue-add]")?.addEventListener("click", addIssue);
    extras.querySelector("[data-issue-input]")?.addEventListener("keydown", (e) => { if (e.key === "Enter") addIssue(); });
    extras.querySelectorAll("[data-issue-remove]").forEach((b) => b.addEventListener("click", () => setIssues((thread.linked_issues || []).filter((n) => n !== Number(b.dataset.issueRemove)), b)));
    extras.querySelector("[data-note-add]")?.addEventListener("click", addNote);
  }
  const timeline = document.getElementById("inboxCustomerTimeline");
  if (timeline) {
    timeline.innerHTML = customerLinkHTML(thread) + timelineHTML(cache.timeline, { withThreads: false });
    timeline.querySelector("[data-customer-link]")?.addEventListener("click", (e) => linkCustomer(e.currentTarget));
    timeline.querySelector("[data-customer-link-input]")?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") linkCustomer(timeline.querySelector("[data-customer-link]"));
    });
    timeline.querySelector("[data-customer-unlink]")?.addEventListener("click", (e) => linkCustomer(e.currentTarget, { unlink: true }));
    timeline.querySelectorAll("[data-open-thread]").forEach((b) => b.addEventListener("click", () => onChange?.({ open: b.dataset.openThread })));
  }
  const banner = document.getElementById("inboxSnoozeBanner");
  if (banner) {
    const html = snoozeBannerHTML(thread);
    banner.innerHTML = html;
    banner.hidden = !html;
    banner.querySelector("[data-unsnooze]")?.addEventListener("click", (e) => unsnooze(e.currentTarget));
  }
}

let headerBound = false;
function bindHeader() {
  if (headerBound) return;
  headerBound = true;
  document.getElementById("inboxSnoozeBtn")?.addEventListener("click", () => toggleSnoozePanel());
}

/** The thread's page closed or another opened: the snooze panel closes, and the next opening reads afresh. */
export function resetThreadExtras() {
  toggleSnoozePanel(false);
  cache = { threadId: null, events: null, timeline: null };
}

function toggleSnoozePanel(show) {
  const panel = document.getElementById("inboxSnoozePanel");
  const btn = document.getElementById("inboxSnoozeBtn");
  if (!panel) return;
  const open = show === undefined ? panel.hidden : show;
  panel.hidden = !open;
  btn?.setAttribute("aria-expanded", String(open));
  if (!open) return;
  panel.innerHTML = snoozePanelHTML();
  panel.querySelectorAll("[data-snooze-preset]").forEach((b) => b.addEventListener("click", () => snooze(b.dataset.snoozePreset, b)));
  panel.querySelector("[data-snooze-cancel]")?.addEventListener("click", () => toggleSnoozePanel(false));
  panel.querySelector("[data-snooze-custom-go]")?.addEventListener("click", () => {
    const value = panel.querySelector("[data-snooze-custom]").value;
    if (!value) { ctx.showToast?.("Pick a date and time first.", "warning"); return; }
    snooze(new Date(value).toISOString(), panel.querySelector("[data-snooze-custom-go]"));
  });
}

async function loadEvents(id) {
  const { db, collection, query, orderBy, getDocs } = ctx;
  try {
    const snap = await getDocs(query(collection(db, "inbox_threads", id, "events"), orderBy("at", "asc")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("⚠️ Thread history unreadable:", err);
    return [];
  }
}

async function loadTimeline(id) {
  try {
    return await call("getInboxCustomerTimeline", { threadId: id });
  } catch (err) {
    console.warn("⚠️ Customer timeline unavailable:", err);
    return { email: thread?.customer?.email || null, threads: [], lead: null, feedback: [] };
  }
}

/** The panel's choices while a snooze is being set: the one pressed spins, the others wait. */
function snoozePanelButtons() {
  return [...(document.getElementById("inboxSnoozePanel")?.querySelectorAll("button") || [])];
}

async function snooze(untilIso, button = null) {
  const note = document.querySelector("#inboxSnoozePanel [data-snooze-note]")?.value || "";
  const threadId = thread.id;
  await withBusy(button, async () => {
    try {
      const res = await call("snoozeInboxThread", { threadId, until: untilIso, note });
      toggleSnoozePanel(false);
      ctx.showToast?.(`Snoozed until ${escapeHTML(formatWhen(res.until))}.`, "success");
      onChange?.({ refresh: true });
    } catch (err) {
      ctx.showToast?.(`Couldn't snooze it: ${escapeHTML(err.message)}`, "error");
    }
  }, { others: snoozePanelButtons() });
}

async function unsnooze(button = null) {
  await withBusy(button, async () => {
    try {
      const res = await call("unsnoozeInboxThread", { threadId: thread.id });
      ctx.showToast?.(`Back in ${escapeHTML(STATUS_WORDS[res.status] || res.status)}.`, "success");
      onChange?.({ refresh: true });
    } catch (err) {
      ctx.showToast?.(`Couldn't unsnooze it: ${escapeHTML(err.message)}`, "error");
    }
  });
}

function addIssue() {
  const input = document.querySelector("#inboxThreadExtras [data-issue-input]");
  const n = Number(String(input?.value || "").replace(/^#/, "").trim());
  if (!Number.isInteger(n) || n <= 0) {
    ctx.showToast?.("Type a ticket number, like 1379.", "warning");
    return;
  }
  setIssues([...(thread.linked_issues || []), n], document.querySelector("#inboxThreadExtras [data-issue-add]"));
}

/** Every ticket control waits while one change runs: the list each one sends is built from the same starting list. */
async function setIssues(issues, button = null) {
  const others = [...(document.querySelectorAll("#inboxThreadExtras [data-issue-add], #inboxThreadExtras [data-issue-remove]") || [])];
  const current = thread;
  await withBusy(button, async () => {
    try {
      const res = await call("setInboxThreadIssues", { threadId: current.id, issues });
      current.linked_issues = res.issues;
      onChange?.({ refresh: true });
    } catch (err) {
      ctx.showToast?.(`Couldn't change the tickets: ${escapeHTML(err.message)}`, "error");
    }
  }, { others });
}

/** Links the open review to the account the field names, or unlinks it; then reads its history and timeline again. */
async function linkCustomer(button, { unlink = false } = {}) {
  const current = thread;
  const text = (document.querySelector("#inboxCustomerTimeline [data-customer-link-input]")?.value || "").trim();
  if (!unlink && !text) {
    ctx.showToast?.("Type their email or user ID.", "warning");
    return;
  }
  const data = { threadId: current.id, ...(unlink ? {} : text.includes("@") ? { email: text } : { uid: text }) };
  await withBusy(button, async () => {
    try {
      const res = await call("linkInboxThreadCustomer", data);
      current.user_uid = res.linked_customer?.uid || null;
      current.linked_customer = res.linked_customer || null;
      ctx.showToast?.(unlink ? "Unlinked." : `Linked to ${escapeHTML(res.linked_customer?.email || res.linked_customer?.uid || "")}.`, "success");
      onChange?.({ refresh: true });
    } catch (err) {
      ctx.showToast?.(`Couldn't ${unlink ? "unlink" : "link"} it: ${escapeHTML(err.message)}`, "error");
    }
  });
}

async function addNote() {
  const input = document.querySelector("#inboxThreadExtras [data-note-input]");
  const text = (input?.value || "").trim();
  if (!text) return;
  const threadId = thread.id;
  await withBusy(document.querySelector("#inboxThreadExtras [data-note-add]"), async () => {
    try {
      await call("addInboxThreadNote", { threadId, text });
      onChange?.({ refresh: true });
    } catch (err) {
      ctx.showToast?.(`Couldn't add the note: ${escapeHTML(err.message)}`, "error");
    }
  });
}
